import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Check, Trash2, Ban, Loader2 } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { REPORT_REASONS } from "@/components/ReportDialog";
import { useUI } from "@/components/Layout";

const reasonLabel = (k) => REPORT_REASONS.find((r) => r.key === k)?.label || k;
const STATUS_STYLE = { open: "bg-[#E024A3]/15 text-[#F472B6]", approved: "bg-emerald-500/15 text-emerald-400", removed: "bg-[#6C0BA9]/25 text-[#C084FC]", banned: "bg-red-500/15 text-red-400" };

function ReportCard({ r, onDone }) {
  const { confirmAction } = useUI();
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState("");
  const act = async (action) => {
    if (action === "ban" && !(await confirmAction({ title: `Ban ${r.target_user?.name}?`, description: "The reported content is removed, they are signed out everywhere and can't log in until unbanned.", actionLabel: "Ban user" }))) return;
    setBusy(action);
    try {
      await api.post(`/admin/reports/${r.id}/resolve`, { action, note });
      toast.success({ approve: "Report dismissed — content kept", remove: "Content removed", ban: "User banned" }[action]);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };
  const snap = r.snapshot || {};
  return (
    <div data-testid={`report-card-${r.id}`} className="bg-card border border-border rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="px-2 py-0.5 rounded-full bg-secondary font-semibold uppercase tracking-wide">{r.target_type.replace("_", " ")}</span>
        <span className="font-semibold text-[#F472B6]">{reasonLabel(r.reason)}</span>
        {r.same_target_count > 1 && <span data-testid={`report-count-${r.id}`} className="text-amber-300">· {r.same_target_count} reports</span>}
        <span className={`ml-auto px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[r.status]}`}>{r.status}</span>
      </div>
      <div className="flex items-center gap-2">
        <Avatar user={r.target_user} size={32} />
        <div className="text-sm min-w-0">
          <div className="font-semibold truncate">{r.target_user?.name || "Unknown"}{r.target_user?.banned && <span className="ml-1 text-xs text-red-400">(banned)</span>}</div>
          <div className="text-xs text-muted-foreground">reported by {r.reporter?.name} · {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</div>
        </div>
      </div>
      {(snap.content || snap.image_url) && (
        <div className="rounded-xl bg-secondary p-3 text-sm space-y-2">
          {snap.content && <p className="whitespace-pre-wrap break-words">{snap.content}</p>}
          {snap.image_url && <img src={mediaUrl(snap.image_url)} alt="" className="max-h-48 rounded-lg object-cover" />}
        </div>
      )}
      {r.details && <p className="text-xs text-muted-foreground italic">"{r.details}"</p>}
      {r.status === "open" ? (
        <>
          <input data-testid={`report-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional)" className="w-full bg-input border border-border rounded-xl px-3 py-2 text-xs outline-none focus:border-[#6C0BA9]" />
          <div className="grid grid-cols-3 gap-2">
            <Button data-testid={`report-approve-${r.id}`} onClick={() => act("approve")} disabled={!!busy} variant="outline" size="sm" className="rounded-xl gap-1">{busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Approve</Button>
            <Button data-testid={`report-remove-${r.id}`} onClick={() => act("remove")} disabled={!!busy || r.target_type === "user"} size="sm" className="rounded-xl gap-1 bg-[#6C0BA9] hover:bg-[#8012C6] text-white">{busy === "remove" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}Remove</Button>
            <Button data-testid={`report-ban-${r.id}`} onClick={() => act("ban")} disabled={!!busy || r.target_user?.is_admin} size="sm" className="rounded-xl gap-1 bg-red-600 hover:bg-red-700 text-white">{busy === "ban" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}Ban</Button>
          </div>
        </>
      ) : r.resolved_note ? <p className="text-xs text-muted-foreground">Note: {r.resolved_note}</p> : null}
    </div>
  );
}

export default function ReportsQueue() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("open");
  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ["admin-reports", status],
    queryFn: async () => (await api.get(`/admin/reports?status=${status}`)).data,
  });
  const done = () => { refetch(); qc.invalidateQueries({ queryKey: ["admin-stats"] }); qc.invalidateQueries({ queryKey: ["admin-log"] }); };
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {["open", "resolved", "all"].map((s) => (
          <button key={s} data-testid={`reports-filter-${s}`} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${status === s ? "bg-[#6C0BA9] text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{s}</button>
        ))}
      </div>
      {isLoading ? <Loader2 className="w-6 h-6 animate-spin text-[#8012C6] mx-auto" /> : data.length === 0 ? (
        <div data-testid="reports-empty" className="bg-card border border-border rounded-2xl p-10 text-center text-sm text-muted-foreground">No {status === "all" ? "" : status} reports. All clear.</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">{data.map((r) => <ReportCard key={r.id} r={r} onDone={done} />)}</div>
      )}
    </div>
  );
}
